<template>
	<AppLayout
		:title="trans('admin.users.representatives.certifications.create.title', {years: group.years})"
		:return-url="route('association.representatives.certifications.index')"
		:return-label="trans('admin.users.representatives.certifications.title')"
	>
		<Modal :id="modalId">
			<template #title>{{ trans('admin.certifications.confirm') }}</template>
			<Alert :color="Color.Amber">{{ trans('admin.certifications.warning') }}</Alert>
			<LabeledObject>
				<template #label>{{ trans('account.types.representative') }}</template>
				<p v-if="certification.representatives.length > 0">{{ certification.representatives.map((m) => m.user.full_name).join(', ') }}.</p>
				<p v-else>{{ trans('general.n_a') }}.</p>
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="create">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<PrimaryButton v-if="certification.certificate_id > 0 && certification.representatives.length > 0" :color="Color.Green" @click.stop="showConfirmModal">
				{{ trans('general.continue') }}
				<i class="fa-solid fa-arrow-right ml-2"/>
			</PrimaryButton>
		</template>
		<LabeledObject class="place-self-start" required>
			<template #label>{{ trans('admin.certifications.certificate') }}</template>
			<select id="select-certificate" v-model="certification.certificate_id">
				<option :value="0">{{ trans('general.please_select') }}</option>
				<option v-for="certificate in certificates" :key="`certificate-${certificate.id}`" :value="certificate.id">{{ certificate.title }}</option>
			</select>
		</LabeledObject>
		<p class="font-bold">{{ trans('account.types.representative') }}</p>
		<CardGrid>
			<Card
				v-for="eligibleRepresentative in eligibleRepresentatives"
				:key="`eligible-member-${eligibleRepresentative.id}`"
				:title="eligibleRepresentative.user.full_name"
				:subtitle="eligibleRepresentative.college.name"
				@click.stop="toggleMember(eligibleRepresentative)"
			>
				<i v-if="certification.representatives.map((m) => m.id).includes(eligibleRepresentative.id)" class="fa-solid fa-toggle-on text-green-600"/>
				<i v-else class="fa-solid fa-toggle-off text-red-600"/>
			</Card>
		</CardGrid>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {defineProps, type Ref, ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Alert from '@/Components/UI/Alerts/Alert.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type Certificate} from '@/Types/Certificates/Certificate'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type Representative} from '@/Types/Association/Representatives/Representative'
import {closeModal, showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'
import {type BoardRepresentativeGroup} from '@/Types/Association/Representatives/BoardRepresentativeGroup'

interface CertificationRepresentation {
	certifiable_type:string,
	certifiable_id:number,
	certificate_id:number,
	representatives:Array<Representative>,
}

const props = defineProps<{
	group:BoardRepresentativeGroup,
	certificates:Array<Certificate>,
	eligibleRepresentatives:Array<Representative>,
}>()

const working:Ref<boolean> = ref(false)
const group:Ref<BoardRepresentativeGroup> = ref(props.group)
const certificates:Ref<Array<Certificate>> = ref(props.certificates)
const eligibleRepresentatives:Ref<Array<Representative>> = ref(props.eligibleRepresentatives)
const modalId:Ref<string> = ref('confirm-certifications-create-modal')
const certification:Ref<CertificationRepresentation> = ref({
	certifiable_type: 'boardRepresentativeGroup',
	certifiable_id: group.value.id,
	certificate_id: 0,
	representatives: [],
})

function showConfirmModal():void{
	showModal(modalId.value)
}

function toggleMember(member:Representative):void{
	const index = certification.value.representatives.findIndex((m: Representative) => m.id === member.id)
	if(index === -1){
		certification.value.representatives.push(member)
	} else {
		certification.value.representatives.splice(index, 1)
	}
}

function create():void{
	if(!working.value){
		closeModal(modalId.value)
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('association.representatives.certifications.store', group.value.id), certification.value).then(() => {
			certification.value.representatives = []
			certification.value.certificate_id = 0
			showSuccess(trans('general.inserted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>